/**
 * Local, no-API keyword matcher for the free-text support chat.
 *
 * The manual only documents a handful of faults and chiller codes, so
 * rather than calling an external AI for every question, we score the
 * operator's question against that fixed knowledge base and return the
 * closest documented match -- instant, free, and works offline. If
 * nothing matches well enough, we say so rather than guessing.
 */

const SAFETY_KEYWORDS = [
  "дым",
  "искр",
  "искрит",
  "удар ток",
  "бьет ток",
  "бьёт ток",
  "горит",
  "загорел",
  "пожар",
  "утечка газа",
  "пахнет газ",
  "запах гар",
  "короткое замыкание",
  "искры",
];

function normalize(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[«»"'.,!?;:()\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(s) {
  return normalize(s)
    .split(" ")
    .filter((w) => w.length >= 3);
}

// Russian words inflect heavily; comparing a short prefix ("stem") of
// each token tolerates most case/number endings without needing a real
// stemmer for this small a vocabulary.
function stem(word) {
  // Short roots (3-4 letters) lose more of their ending to case
  // inflection proportionally, so keep less of them; longer words keep
  // ~60% of their letters.
  if (word.length <= 4) return word.slice(0, Math.max(2, word.length - 1));
  return word.slice(0, Math.ceil(word.length * 0.6));
}

function scoreText(tokens, text) {
  const norm = normalize(text);
  let score = 0;
  tokens.forEach((t) => {
    if (norm.includes(stem(t))) score += 1;
  });
  return score;
}

function isSafetyQuestion(question) {
  const norm = normalize(question);
  return SAFETY_KEYWORDS.some((k) => norm.includes(k));
}

function matchCode(question, codes) {
  const norm = normalize(question);
  return codes.find((c) =>
    c.code
      .toLowerCase()
      .split("/")
      .some((part) => part.length >= 2 && norm.includes(part.toLowerCase()))
  );
}

function formatFaultAnswer(fault) {
  return (
    "Возможные причины:\n" +
    fault.causes.map((c, i) => `${i + 1}. ${c}`).join("\n") +
    "\n\nСпособ устранения:\n" +
    fault.fixes.map((c, i) => `${i + 1}. ${c}`).join("\n")
  );
}

/**
 * @returns {{text: string, source: string|null, isSafety: boolean}}
 */
export function findAnswer(question, faults, codes) {
  const isSafety = isSafetyQuestion(question);

  const codeHit = matchCode(question, codes || []);
  if (codeHit) {
    return { text: codeHit.meaning, source: codeHit.source, isSafety };
  }

  const tokens = tokenize(question);
  let best = null;
  let bestScore = 0;
  (faults || []).forEach((f) => {
    const searchText = [f.title, ...(f.causes || []), ...(f.fixes || [])].join(" ");
    const score = scoreText(tokens, searchText);
    if (score > bestScore) {
      bestScore = score;
      best = f;
    }
  });

  if (best && bestScore >= 1) {
    return { text: formatFaultAnswer(best), source: best.source, isSafety };
  }

  return {
    text:
      "Точного совпадения в руководстве не найдено. Выберите один из пунктов слева, " +
      "либо, если похоже на угрозу безопасности, остановите работу и обратитесь к специалисту.",
    source: null,
    isSafety,
  };
}
